import type { GetStaticPaths, GetStaticProps } from 'next';
import React, { useEffect } from 'react';

import Layout from '../../components/Layout';
import SupportPage from '../../components/PageTypes/Support';
import { SUPPORT_PAGE_TYPE } from '../../constants';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { TrackPageView } from '../../utils/analytics';
import getPaths from '../../utils/getPaths';
import { getStoryblokPage, useStoryblok } from '../../utils/storyblok';
import { pageTranslations } from '../../utils/translations';

const SUPPORT_ROOT_PATH = 'support';

export default function Page({
  locale,
  locales,
  ogImageUrl,
  params,
  preview,
  story,
}: PageProps) {
  useEffect(() => {
    TrackPageView({ page: SUPPORT_PAGE_TYPE });
  }, []);

  const enableBridge = preview;
  const slug = params.slug ? params.slug.join('/') : 'home';

  story = useStoryblok(story, enableBridge, locale);

  return (
    <Layout
      description={story.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={story.content.seo?.title}
    >
      <SupportPage blok={story.content} slug={slug} />
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async ({
  locale,
  locales,
  params,
  preview = false,
}) => {
  const slug = params?.slug ? (params.slug as string[]).join('/') : 'home';
  const page = await getStoryblokPage(
    `cdn/stories/${SUPPORT_ROOT_PATH}/${slug}`,
    { locale, preview },
  );

  if (page === null) {
    return {
      notFound: true,
    };
  }

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: {
      key: 'og:support.title',
      options: {
        title: page.data.story.content.title,
      },
    },
    locale,
  });

  return {
    props: {
      story: page.data ? page.data.story : false,
      preview,
      locale,
      locales,
      params,
      ogImageUrl,
      ...(await pageTranslations(locale!, ['support', 'og'])),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  return {
    paths: await getPaths(locales!, SUPPORT_ROOT_PATH),
    fallback: false,
  };
};
